import AlcoholCalcNavbar from "../Navbars/AlcoholCalcNavbar";
import React, {useState} from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {getCookie} from "../../tools/tools";
import Plot from 'react-plotly.js';


const AlcoholCalcV3 = () => {
    const [formData, setFormData] = useState({
        number_of_alcohol_intake: 1,
        drinks: [
            {
                V: 500,
                z: 0.4,
                t: 2,
                a: 2,
                d: 0.3,
            }
        ],
        M: 100,
        b: 0.15,
        r: 0.7,
    });
    const [plotData, setPlotData] = useState(null);
    const names = {
        number_of_alcohol_intake: 'Number OfAlcohol Intake',
        'V': 'Volume of the drink, ml',
        'z': 'Strength of the drink, d.u.',
        'M': 'Mass of the subject, kg',
        't': 'Time after alcohol intake, hours',
        'r': 'Distribution coefficient d.u. or l/kg',
        'a': 'Absorption constant, 1/h',
        'b': 'Elimination rate (β), g/kg/h or g/l/h',
        'd': 'Extraction constant, 1/h',
    }

    const [result, setResult] = useState(null);
    const [errors, setErrors] = useState([]);

    const validationRules = {
        number_of_alcohol_intake: {min: 1, max: 10},
        V: {min: 0, max: 10000},
        z: {min: 0, max: 1},
        M: {min: 1e-10, max: 200},
        t: {min: 1e-10, max: 1e8},
        r: {min: 1e-10, max: 1,},
        b: {min: 1e-10, max: 0.7},
        a: {min: 1, max: 8},
        d: {min: 1e-10, max: 0.6},
    };

    const [isValidForm, setIsValidForm] = useState(true);
    const checkFormValidity = () => {
        return Object.keys(errors).length === 0;
    };

    const handleNumberChange = (e) => {
        const count = parseInt(e.target.value) || 1;
        if (errors['number_of_alcohol_intake'])
            delete errors['number_of_alcohol_intake'];
        setIsValidForm(checkFormValidity());
        if (count > 10) {
            const newErrors = {...errors};
            newErrors['number_of_alcohol_intake'] = 'Number of alcohol intake must be between 1 and 10'
            setErrors(newErrors);
            setFormData({
                ...formData,
                number_of_alcohol_intake: count,
            });
            setIsValidForm(false);
            return;
        }

        const newDrinks = [];

        for (let i = 0; i < count; i++) {
            newDrinks[i] = formData.drinks[i] || {
                V: 500,
                z: 0.4,
                t: 2,
                a: 2,
                d: 0.3,
            };
        }
        setFormData({
            ...formData,
            number_of_alcohol_intake: count,
            drinks: newDrinks
        });
    };

    const handleDrinkChange = (index, field, value) => {
        const numericValue = parseFloat(value);
        const newDrinks = [...formData.drinks];
        const newErrors = {...errors};

        if (newErrors[index]?.[field]) {
            delete newErrors[index][field];

            if (Object.keys(newErrors[index]).length === 0) {
                delete newErrors[index];
            }
        }

        const rules = validationRules[field];
        if (rules) {
            if (numericValue < rules.min || numericValue > rules.max || isNaN(numericValue)) {
                newErrors[index] = {
                    ...newErrors[index],
                    [field]: `must be between ${rules.min} and ${rules.max}`
                };
            }
        }
        newDrinks[index][field] = numericValue;

        setFormData({
            ...formData,
            drinks: newDrinks
        });
         const hasErrors = Object.keys(newErrors).length > 0;
         setIsValidForm(!hasErrors);
        setErrors(newErrors);
    };

    const handleSubmit = async () => {
        try {
            const requestData = {
                M: formData.M,
                r: formData.r,
                b: formData.b,
                drinks: formData.drinks.map(drink => ({
                    V: drink.V,
                    z: drink.z,
                    a: drink.a,
                    d: drink.d,
                    t: drink.t
                }))
            };
            const data = JSON.stringify(requestData, null, 2)
            const response = await axios.post('/api/alcohol-calcs/3', data, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken')
                },
                withCredentials: true,
            });

            if (response.data.errors && response.data.errors.length > 0) {
                setErrors(response.data.errors);
                setResult(null);
                setPlotData(null);
            } else {
                setErrors([]);
                setPlotData(null);
                setResult(response.data);
                console.log(response.data);
                if (response.data.bac_points) {
                    const plotPoints = {
                        x: response.data.bac_points.map(point => point.time),
                        y: response.data.bac_points.map(point => point.bac),
                        config: {
                            title: 'BAC over Time',
                            xlabel: 'Time (hours)',
                            ylabel: 'Blood Alcohol Concentration'
                        }
                    };
                    setPlotData(plotPoints);
                }
            }
        } catch (error) {
            console.error('Error occurred:', error);
            setErrors([]);
            setPlotData(null);
            setResult(null);
            setErrors(['An error occurred while processing the request']);
        }
    };

    return (
        <div className="content">
            <AlcoholCalcNavbar/>
            <div className="section section-components" style={{paddingTop: 40}}>
                <div className="container mb-5 pb-100">
                    <h1 className="h1 text-success font-weight-bold mb-4"> Multi-Dose Oral Ethanol Blood Kinetics Calculator</h1>
                    <div className="form-group row">
                        <label className="col-sm-4 col-form-label">
                            {names.number_of_alcohol_intake}
                        </label>
                        <div className="col-sm-4">
                            <input
                                type="number"
                                className="form-control"
                                value={formData.number_of_alcohol_intake}
                                onChange={handleNumberChange}
                                min="1"
                                max="10"
                            />
                        </div>
                    </div>
                    <div className="row">
                        <label className="col-sm-3 text-danger">
                            {errors['number_of_alcohol_intake']}
                        </label>
                    </div>
                    <div className="form-group row">
                        <label className="col-sm-4 col-form-label">
                            {names.M}
                        </label>
                        <div className="col-sm-4">
                            <input
                                type="number"
                                className="form-control"
                                value={formData.M}
                                onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                const newErrors = {...errors};

                                if (value < validationRules.M.min || value > validationRules.M.max) {
                                    newErrors[names.M] = `Mass of subject must be between: ${validationRules.M.min} - ${validationRules.M.max}`;
                                    setIsValidForm(false);
                                } else {
                                    delete newErrors[names.M];
                                    setIsValidForm(Object.keys(newErrors).length === 0);
                                }

                                setFormData({...formData, M: value});
                                setErrors(newErrors);
                            }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <label className="col-sm-3 text-danger">
                            {errors[names.M]}
                        </label>
                    </div>
                    <div className="form-group row">
                        <label className="col-sm-4 col-form-label">
                            {names.b}
                        </label>
                        <div className="col-sm-4">
                            <input
                                type="number"
                                className="form-control"
                                value={formData.b}
                                step={0.01}
                                onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                const newErrors = {...errors};

                                if (value < validationRules.b.min || value > validationRules.b.max) {
                                    newErrors[names.b] = `${names.b} must be between : ${validationRules.b.min} - ${validationRules.b.max}`;
                                    setIsValidForm(false);
                                } else {
                                    delete newErrors[names.b];
                                    setIsValidForm(Object.keys(newErrors).length === 0);
                                }
                                setFormData({...formData, b: value});
                                setErrors(newErrors);
                            }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <label className="col-sm-3 text-danger">
                            {errors[names.b]}
                        </label>
                    </div>
                    <div className="form-group row">
                        <label className="col-sm-4 col-form-label">
                            {names.r}
                        </label>
                        <div className="col-sm-4">
                            <input
                                type="number"
                                className="form-control"
                                value={formData.r}
                                step={0.01}
                                onChange={(e) => {
                                    const value = parseFloat(e.target.value) || 0;
                                    const newErrors = {...errors};

                                    if (value < validationRules.r.min || value > validationRules.r.max) {
                                        newErrors[names.r] = `${names.r} must be between: ${validationRules.r.min} - ${validationRules.r.max}`;
                                        setIsValidForm(false);
                                    } else {
                                        delete newErrors[names.r];
                                        setIsValidForm(Object.keys(newErrors).length === 0);
                                    }
                                    setFormData({...formData, r: value});
                                    setErrors(newErrors);
                                }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <label className="col-sm-3 text-danger">
                            {errors[names.r]}
                        </label>
                    </div>
                    {formData.drinks.map((drink, index) => (
                        <div key={index} className="card mb-3">
                            <div className="card-header">
                                <h5>Alcohol Intake #{index + 1}</h5>
                            </div>
                            <div className="card-body">
                                {['V', 'z', 't', 'a', 'd'].map((field) => (
                                    <div className="form-group row" key={field}>
                                        <label className="col-sm-4 col-form-label">
                                            {names[field]}
                                        </label>
                                        <div className="col-sm-4">
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={drink[field]}
                                                onChange={(e) =>
                                                    handleDrinkChange(index, field, e.target.value)
                                                }
                                                step={field === 'z' || field === 'd' ? 0.01 : 1}
                                            />
                                        </div>
                                    </div>
                                ))}
                                {errors[index] && Object.entries(errors[index]).map(([field, message]) => (
                                    <div key={field} className="col-sm-3 text-danger">
                                        {names[field]}: {message}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    <button
                        onClick={handleSubmit}
                        className="btn btn-primary btn-lg"
                        disabled={!isValidForm}
                    >
                        Calculate
                    </button>
                    <div className="mt-2">
                        <small className="text-muted">
                            By clicking "Calculate" you agree to the{' '}
                            <Link to="/agreement">User Agreement</Link>
                            .
                        </small>
                    </div>
                    {result && (
                        <div className="mt-5">
                            <h2 className="text-muted mb-3">Calculation Results</h2>
                            <div>
                                <h2>Amount of alcohol consumed</h2>
                                <p>Blood ethanol concentration = {result['bac']}, ‰</p>
                                <p>Presystemic metabolism of ethanol {result['total_eliminated']}, g </p>
                                <p>Absorbed ethanol {result['total_absorbed']}, g</p>
                                <p>Ethanol elimination {result['total_metabolized']} g</p>
                                <p>Time after the end of ethanol intake {result['last_drink_time']}, hours</p>
                                <p>Time after the start of ethanol intake, hours {result['total_elapsed_time']},
                                    hours</p>
                            </div>
                            {plotData && (<div style={{width: '100%', height: '80vh'}}>
                                <Plot
                                    data={[
                                        {
                                            x: plotData.x,
                                            y: plotData.y,
                                            type: 'scatter',
                                            mode: 'lines',
                                            line: {
                                                color: '#2ca02c',
                                                width: 2},
                                            name:'BAC(t)',
                                        },
                                    ]}
                                    layout={{
                                        title: plotData.config?.title || 'Function of t',
                                        xaxis: {
                                            title: plotData.config?.x_label || 't',
                                            gridcolor: '#eee',
                                            zerolinecolor: '#ccc'
                                        },
                                        yaxis: {
                                            title: plotData.config?.y_label || 'C(t)',
                                            gridcolor: '#eee',
                                            zerolinecolor: '#ccc'
                                        },
                                        plot_bgcolor: '#f9f9f9',
                                        paper_bgcolor: '#f9f9f9',
                                        hovermode: 'closest',
                                        showlegend: true
                                    }}
                                    config={{
                                        responsive: true,
                                        displayModeBar: true,
                                        scrollZoom: true
                                    }}
                                    style={{width: '100%', height: '100%'}}
                                />
                            </div>)}
                        </div>
                    )}


                    {errors.length > 0 && (
                        <div className="alert alert-danger mt-4">
                            {errors.map((error, idx) => (
                                <div key={idx}>{error}</div>
                            ))}
                        </div>
                    )}

                </div>
            </div>
        </div>
    )
};

export default AlcoholCalcV3;